import React, {MouseEventHandler, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'
import Checkbox from '@material-ui/core/Checkbox'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import {
  checkAvailabilityNorm3DApi,
  reAnalysisIndividualNorm3DApi,
  SearchedIndividualNorm,
} from 'api/analysisApi'
import ActionButton from 'components/atoms/Buttons/ActionButton'
import TableButton from 'components/atoms/Buttons/TableButton'
import {ClientName} from 'components/atoms/ClientName'
import Pagination from 'components/molcules/Pagination'
import TableCell from 'components/Table/TableCell'
import TableReportSearch from 'components/Table/TableReportSearch'
import useStyles from 'components/Table/useTableStyles'
import useToolbarStyles from 'components/Table/useTableToolbarStyles'
import {AnalysisReportType} from 'constants/AnalysisConstant'
import RouteConstant from 'constants/RouteConstant'
import useEegNormSearch from 'features/analysis/useEegNormSearch'
import useFailureModal from 'features/modal/useFailureModal'
import useNoticeConfirm from 'features/modal/useNoticeConfirm'
import useSuccessModal from 'features/modal/useSuccessModal'
import {
  getEyeStatus,
  isReadDisable,
  statusToI18nString,
} from 'helpers/analysisHelper'
import {
  dateToPeriodString,
  getDefaultPeriodDate,
  isoStringToDateAndTimeString,
} from 'helpers/dateHelper'
import openInNewTab from 'helpers/openInNewTab'
import {getQueryFromUrl} from 'helpers/commonHelper'
import useProductManagement from 'features/productManagement/useProductManagement'
import {
  MCI_CLINIC_INDICATOR_ID,
  MCI_INDICATOR_ID,
} from 'pages/ProductsPages/constant'

interface TableToolBarProps {
  selectedId: number
  isShowAnalysisButton: boolean
}

function TableToolbar({selectedId, isShowAnalysisButton}: TableToolBarProps) {
  const location = useLocation()
  const {t} = useTranslation()
  const classes = useToolbarStyles()
  const {query, search, onSearch} = useEegNormSearch()
  const {onOpen: onSuccessModalOpen} = useSuccessModal()
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {onOpen: onNoticeDialogOpen} = useNoticeConfirm()

  const handleReanalysis3DNormClick = async () => {
    try {
      const isAvailableResponse = await checkAvailabilityNorm3DApi(selectedId)
      if (!isAvailableResponse.data.available) {
        onFailureModalOpen(String(isAvailableResponse.data.message))
      } else {
        await reAnalysisIndividualNorm3DApi(selectedId)
        onSuccessModalOpen(t('IProcessSuccess'))
        onSearch(query)
      }
    } catch (error) {
      onFailureModalOpen(String(error.message))
    }
  }

  const confirm3DNormAnalysis = async () => {
    if (selectedId === 0) {
      onFailureModalOpen(t('ISelectRequired'))
      return
    }

    onNoticeDialogOpen({
      title: t('INoticeTitle'),
      message: t('INoticeConfirmDesc'),
    }).then((result) => {
      if (result.payload) handleReanalysis3DNormClick()
    })
  }

  /** Set default value for search query if it is stored in url */
  const selection = getDefaultPeriodDate()

  useEffect(() => {
    const initQuery = getQueryFromUrl(location.search)
    onSearch({
      ...query,
      period: dateToPeriodString(selection),
      paging: {page: 0, size: 10},
      ...initQuery,
    })
  }, [])

  return (
    <div className={classes.root}>
      <TableReportSearch
        reportType={AnalysisReportType.EEGNorm}
        isRoot={false}
        query={query}
        search={search}
        onSearch={onSearch}
      />
      {isShowAnalysisButton && (
        <div className={classes.containerOptional}>
          <ActionButton onClick={confirm3DNormAnalysis}>
            {t('IView3D')}
          </ActionButton>
        </div>
      )}
    </div>
  )
}

interface RenderTableRowProps {
  item: SearchedIndividualNorm
  onClick: (item: SearchedIndividualNorm) => void
  isSelected: (item: SearchedIndividualNorm) => boolean
}

function RenderTableRow({item, onClick, isSelected}: RenderTableRowProps) {
  const {t} = useTranslation()
  const handleResultClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    const clientName = {
      firstName: item.firstName ?? '',
      lastName: item.lastName ?? '',
    }
    openInNewTab(
      `${RouteConstant.ANALYSIS_EEG_19_N.path}/report/${
        item.id
      }?clientName=${encodeURIComponent(JSON.stringify(clientName))}&version=${
        item.normdbVersion
      }`,
    )
  }

  const labelId = `enhanced-table-checkbox-${item.id}`

  const checked = isSelected(item)

  const handleClick = () => onClick(item)

  return (
    <TableRow hover onClick={handleClick}>
      <TableCell padding='checkbox'>
        <Checkbox
          disabled={isReadDisable(item.progress)}
          checked={checked}
          inputProps={{'aria-labelledby': labelId}}
        />
      </TableCell>
      <TableCell align='center'>{item.id}</TableCell>
      <TableCell align='center'>{item.individualId}</TableCell>
      <TableCell align='center'>
        <ClientName
          firstName={item.firstName}
          lastName={item.lastName}
          birth={item.birth}
          gender={item.gender}
        />
      </TableCell>
      <TableCell align='center'>{getEyeStatus(item.eceo)}</TableCell>
      <TableCell align='center'>{item.fileName}</TableCell>
      <TableCell align='center'>
        {isoStringToDateAndTimeString(String(item.measureDate))}
      </TableCell>
      <TableCell align='center'>{item.normdbVersion || '-'}</TableCell>
      <TableCell align='center'>{item.zscore}</TableCell>
      <TableCell align='center'>
        {t(statusToI18nString(item.progress))}
      </TableCell>
      <TableCell align='center'>
        {t(statusToI18nString(item.additionalAnalysis))}
      </TableCell>
      <TableCell align='center'>
        <TableButton
          onClick={handleResultClick}
          disabled={item.progress !== 'SUCCESS'}
        >
          {t('IRead')}
        </TableButton>
      </TableCell>
    </TableRow>
  )
}

function PageTable() {
  const {getAvailableLicenses, listAvailableLicenses} = useProductManagement()
  const {t} = useTranslation()
  const classes = useStyles()

  const {loading, query, paging, pagingInfo, items, onSearch} =
    useEegNormSearch()
  const [selectedId, setSelectedId] = useState<number>(0)

  const emptyRows = items === null ? 5 : 5 - items.length

  const setPageIndex = (page: number) => {
    onSearch({
      ...query,
      paging: {
        page,
        size: paging.size,
      },
    })
  }

  const onSizeChange = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    const size = parseInt(event.target.value as string, 10)
    onSearch({
      ...query,
      paging: {
        page: 0,
        size,
      },
    })
  }

  const handleRowClick = (item: SearchedIndividualNorm) => {
    if (item.progress !== 'SUCCESS') {
      return
    }

    setSelectedId(item.id)
  }

  const isRowSelected = (item: SearchedIndividualNorm) => {
    return selectedId === item.id
  }

  const isShowAnalysisButton = useMemo(() => {
    return Array.from(
      new Set(listAvailableLicenses.map((license) => license.packageItemId)),
    ).some(
      (packageId) =>
        packageId === MCI_CLINIC_INDICATOR_ID || packageId === MCI_INDICATOR_ID,
    )
  }, [listAvailableLicenses])

  useEffect(() => {
    getAvailableLicenses()
  }, [])

  return (
    <TableContainer className={classes.tableContainer}>
      <TableToolbar
        selectedId={selectedId}
        isShowAnalysisButton={isShowAnalysisButton}
      />
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.tableHeader}>
            <TableCell align='center' padding='none' />
            <TableCell align='center' padding='none'>
              {t('IEEGNormDbIndex')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IIndividualIndex')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IPatientName')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IECEO')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IFileName')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IMeasureDate')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IVersion')}
            </TableCell>
            <TableCell align='center' padding='none'>
              z-score
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IProgress')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IView3D')}
            </TableCell>
            <TableCell align='center' padding='none'>
              {t('IAnalysisReport')}
            </TableCell>
            {/* TODO: 놈분석 3D 요청하는 버튼 추가 */}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <RenderTableRow
              key={item.id}
              item={item}
              onClick={handleRowClick}
              isSelected={isRowSelected}
            />
          ))}
          {emptyRows > 0 && (
            <TableRow
              style={{height: 43 * emptyRows, backgroundColor: '#F9F9FB'}}
            >
              <TableCell colSpan={12} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Pagination
        totalPageCount={pagingInfo.totalPages}
        currentPageIndex={query.paging.page ?? 0}
        itemCountPerPage={query.paging.size ?? 10}
        setCurrentPageIndex={setPageIndex}
        loading={loading}
        onItemCountPerPageChanged={onSizeChange}
      />
    </TableContainer>
  )
}

export default PageTable
